import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { d, fittedSize, type Frame, frameFor, header, note, statusDot, textWidth, TYPE, wrapText } from './kit.js';

export interface TransformItem {
  id: string;
  label: string;
  /** The measured amount entering or leaving, in the author's own notation. */
  amount?: string;
  /** How much of each conserved quantity this item carries. */
  carries?: Record<string, number>;
  /** A driver is consumed by the change but is not one of the conserved things. */
  driver?: boolean;
}

export interface TransformSpec extends PrimitiveSpec {
  family: 'transform';
  view: 'schematic';
  subject: string;
  inputs: TransformItem[];
  process: { label: string; detail: string };
  outputs: TransformItem[];
  /** The quantities the mechanism must preserve, in ledger order. */
  conserved: string[];
  law: string;
  title: string;
  subtitle: string;
}

export const transformItemId = (side: 'input' | 'output', id: string): string => `transform.${side}.${id}`;
export const transformLedgerId = (quantity: string): string => `transform.ledger.${quantity.toLowerCase()}`;
export const TRANSFORM_PROCESS_ID = 'transform.process';

interface Metrics {
  frame: Frame;
  cardH: number;
  cardW: number;
  detailLines: string[];
  lawLines: string[];
  inputsTop: number;
  processTop: number;
  processH: number;
  outputsTop: number;
  ledgerTop: number;
  ledgerRow: number;
  footerY: number;
  required: number;
}

function metrics(spec: TransformSpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const cardH = 54;
  const gap = 34;
  const cardW = Math.min(162, (frame.contentWidth - 10 * (Math.max(spec.inputs.length, spec.outputs.length) - 1)) / Math.max(spec.inputs.length, spec.outputs.length));
  const detailLines = wrapText(spec.process.detail, TYPE.micro, frame.contentWidth - 28, true);
  const lawLines = wrapText(spec.law.toUpperCase(), TYPE.micro, frame.contentWidth, true);
  const inputsTop = frame.top + 14;
  const processTop = inputsTop + cardH + gap;
  const processH = 34 + detailLines.length * 15;
  const outputsTop = processTop + processH + gap;
  const ledgerTop = outputsTop + cardH + 22;
  const ledgerRow = 22;
  const footerY = ledgerTop + 24 + spec.conserved.length * ledgerRow + 20;
  return {
    frame, cardH, cardW, detailLines, lawLines, inputsTop, processTop, processH, outputsTop,
    ledgerTop, ledgerRow, footerY, required: footerY + lawLines.length * 14 + 6,
  };
}

/**
 * A transformation is a bookkeeping claim.
 *
 * Boxes joined by arrows are a flowchart; what makes a figure a transformation
 * is that named things go in, one mechanism rearranges them, named things come
 * out — and some quantity survives the rearrangement unchanged. This compiler
 * refuses to draw a mechanism whose ledger does not balance, and it separates
 * the drivers (consumed, not conserved) from the matter that is only moved.
 */
export function compileTransform(spec: TransformSpec): ResolvedFigure {
  validateTransform(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const warnings: string[] = [];
  const processCentre = frame.margin + frame.contentWidth / 2;

  header(builder, frame, 'transform', `MECHANISM · ${spec.subject.toUpperCase()}`, [
    `${spec.inputs.length} IN · ${spec.outputs.length} OUT · ${spec.conserved.join(', ')} CONSERVED`,
    `${spec.conserved.join(', ')} CONSERVED`,
  ]);

  const drawCards = (items: TransformItem[], kind: 'input' | 'output', top: number): void => {
    const gap = 10;
    const width = m.cardW;
    const rowWidth = items.length * width + (items.length - 1) * gap;
    const rowX = frame.margin + (frame.contentWidth - rowWidth) / 2;
    items.forEach((item, index) => {
      const semanticId = transformItemId(kind, item.id);
      const x = rowX + index * (width + gap);
      const bounds = box(x, top, width, m.cardH);
      // Only the mechanism is the taught element: the cards stay neutral and are
      // told apart by the direction of their flow lines, not by colour.
      builder.rect(`${semanticId}.card`, semanticId, item.driver ? 'conditional' : kind === 'input' ? 'header' : 'record', bounds, {
        radius: 4,
        label: `${item.label}${item.amount ? `: ${item.amount}` : ''}`,
        interactive: true,
      });
      const lines = wrapText(item.label, TYPE.micro, width - 14, true);
      lines.slice(0, 2).forEach((line, lineIndex) => {
        builder.text(`${semanticId}.label.${lineIndex}`, semanticId, 'annotation', point(x + 7, top + 16 + lineIndex * 13), line, {
          size: TYPE.micro,
          mono: true,
          weight: 700,
        });
      });
      if (lines.length > 2) warnings.push(`${item.label} needs more room than this tier allows.`);
      if (item.amount) {
        builder.text(`${semanticId}.amount`, semanticId, 'structure', point(x + 7, top + m.cardH - 12), item.amount, {
          size: fittedSize(item.amount, TYPE.label, width - 14, true),
          mono: true,
          weight: 700,
        });
      }
      // The flow line: every item is joined to the one mechanism, never to another item.
      const from = kind === 'input' ? point(x + width / 2, top + m.cardH) : point(x + width / 2, top);
      const to = kind === 'input' ? point(processCentre, m.processTop) : point(processCentre, m.processTop + m.processH);
      const midY = kind === 'input' ? (from.y + to.y) / 2 : (to.y + from.y) / 2;
      const path = kind === 'input'
        ? `M ${d(from.x)} ${d(from.y)} C ${d(from.x)} ${d(midY)}, ${d(to.x)} ${d(midY)}, ${d(to.x)} ${d(to.y)}`
        : `M ${d(to.x)} ${d(to.y)} C ${d(to.x)} ${d(midY)}, ${d(from.x)} ${d(midY)}, ${d(from.x)} ${d(from.y)}`;
      builder.path(`${semanticId}.flow`, semanticId, item.driver ? 'conditional' : 'operator', path, {
        markerEnd: true,
        ...(item.driver ? { dash: '4 4' } : {}),
      });
      builder.anchor(`${semanticId}.port`, semanticId, from, kind === 'input' ? 'down' : 'up');
      builder.hit(semanticId, bounds);
      builder.relation(
        `${semanticId}.${kind === 'input' ? 'enters' : 'leaves'}`,
        item.driver ? 'drives' : kind === 'input' ? 'enters' : 'leaves',
        kind === 'input' ? semanticId : TRANSFORM_PROCESS_ID,
        kind === 'input' ? TRANSFORM_PROCESS_ID : semanticId,
        `${item.label}${item.amount ? ` (${item.amount})` : ''} ${item.driver ? 'drives' : kind === 'input' ? 'enters' : 'leaves'} ${spec.process.label}`,
      );
    });
  };

  builder.text('transform.inputs.kicker', 'transform', 'annotation', point(frame.margin, m.inputsTop - 8), 'GOES IN', {
    size: TYPE.micro, mono: true, weight: 700, uppercase: true,
  });
  drawCards(spec.inputs, 'input', m.inputsTop);

  const processBounds = box(frame.margin, m.processTop, frame.contentWidth, m.processH);
  builder.rect(`${TRANSFORM_PROCESS_ID}.band`, TRANSFORM_PROCESS_ID, 'active', processBounds, {
    radius: 4,
    label: spec.process.label,
    interactive: true,
  });
  builder.text(`${TRANSFORM_PROCESS_ID}.label`, TRANSFORM_PROCESS_ID, 'structure', point(processCentre, m.processTop + 26), spec.process.label, {
    size: fittedSize(spec.process.label, TYPE.heading, frame.contentWidth - 24, true),
    mono: true,
    weight: 700,
    align: 'middle',
  });
  m.detailLines.forEach((line, index) => {
    builder.text(`${TRANSFORM_PROCESS_ID}.detail.${index}`, TRANSFORM_PROCESS_ID, 'annotation', point(processCentre, m.processTop + 41 + index * 15), line, {
      size: TYPE.micro,
      mono: true,
      weight: 500,
      align: 'middle',
    });
  });
  builder.anchor(`${TRANSFORM_PROCESS_ID}.in`, TRANSFORM_PROCESS_ID, point(processCentre, m.processTop), 'up');
  builder.anchor(`${TRANSFORM_PROCESS_ID}.out`, TRANSFORM_PROCESS_ID, point(processCentre, m.processTop + m.processH), 'down');
  builder.hit(TRANSFORM_PROCESS_ID, processBounds);

  builder.text('transform.outputs.kicker', 'transform', 'annotation', point(frame.margin, m.outputsTop - 8), 'COMES OUT', {
    size: TYPE.micro, mono: true, weight: 700, uppercase: true,
  });
  drawCards(spec.outputs, 'output', m.outputsTop);

  // The ledger: the claim that makes this a transformation rather than a flow.
  const ledgerBounds = box(frame.margin, m.ledgerTop, frame.contentWidth, 24 + spec.conserved.length * m.ledgerRow);
  builder.rect('transform.ledger', 'transform.ledger', 'structure', ledgerBounds, { radius: 4, label: 'conservation ledger', interactive: true });
  builder.rect('transform.ledger.head', 'transform.ledger', 'header', box(frame.margin, m.ledgerTop, frame.contentWidth, 24));
  const columnQuantity = frame.margin + 10;
  const columnIn = frame.margin + frame.contentWidth * 0.46;
  const columnOut = frame.margin + frame.contentWidth * 0.68;
  const columnState = frame.margin + frame.contentWidth - 10;
  builder.text('transform.ledger.head.quantity', 'transform.ledger', 'annotation', point(columnQuantity, m.ledgerTop + 16), 'CONSERVED', { size: TYPE.micro, mono: true, weight: 700, uppercase: true });
  builder.text('transform.ledger.head.in', 'transform.ledger', 'annotation', point(columnIn, m.ledgerTop + 16), 'IN', { size: TYPE.micro, mono: true, weight: 700, align: 'end', uppercase: true });
  builder.text('transform.ledger.head.out', 'transform.ledger', 'annotation', point(columnOut, m.ledgerTop + 16), 'OUT', { size: TYPE.micro, mono: true, weight: 700, align: 'end', uppercase: true });
  builder.text('transform.ledger.head.state', 'transform.ledger', 'annotation', point(columnState, m.ledgerTop + 16), 'STATE', { size: TYPE.micro, mono: true, weight: 700, align: 'end', uppercase: true });
  builder.anchor('transform.ledger.top', 'transform.ledger', point(processCentre, m.ledgerTop), 'up');
  builder.hit('transform.ledger', ledgerBounds);

  spec.conserved.forEach((quantity, index) => {
    const semanticId = transformLedgerId(quantity);
    const y = m.ledgerTop + 24 + index * m.ledgerRow;
    const totals = totalsFor(spec, quantity);
    const balanced = totals.input === totals.output;
    builder.text(`${semanticId}.quantity`, semanticId, 'structure', point(columnQuantity, y + 15), quantity, { size: TYPE.annotation, mono: true, weight: 700 });
    builder.text(`${semanticId}.in`, semanticId, 'structure', point(columnIn, y + 15), String(totals.input), { size: TYPE.annotation, mono: true, weight: 700, align: 'end' });
    builder.text(`${semanticId}.out`, semanticId, 'structure', point(columnOut, y + 15), String(totals.output), { size: TYPE.annotation, mono: true, weight: 700, align: 'end' });
    const state = balanced ? 'BALANCED' : 'BROKEN';
    builder.text(`${semanticId}.state`, semanticId, 'annotation', point(columnState, y + 15), state, { size: TYPE.micro, mono: true, weight: 700, align: 'end', uppercase: true });
    statusDot(builder, `${semanticId}.dot`, semanticId, point(columnState - textWidth(state, TYPE.micro, true, true) - 11, y + 10), balanced ? 'positive' : 'negative');
    builder.anchor(`${semanticId}.row`, semanticId, point(columnQuantity, y + m.ledgerRow / 2), 'left');
    builder.hit(semanticId, box(frame.margin, y, frame.contentWidth, m.ledgerRow));
    builder.relation(`${semanticId}.balances`, 'conserves', TRANSFORM_PROCESS_ID, semanticId, `${quantity}: ${totals.input} in, ${totals.output} out`);
  });

  const drivers = spec.inputs.filter((item) => item.driver);
  m.lawLines.forEach((line, index) => {
    note(builder, `transform.law.${index}`, 'transform', point(frame.margin, m.footerY + index * 14), line);
  });
  if (drivers.length) {
    warnings.push(`${drivers.map((item) => item.label).join(', ')} drives the change and is not conserved by it.`);
  }

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: `conserved:${spec.conserved.join('+')}`,
    active: [TRANSFORM_PROCESS_ID, ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateTransform(spec: TransformSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'transform' || spec.view !== 'schematic') throw new Error('transform: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('transform: provenance is required');
  if (!spec.subject.trim() || !spec.title.trim() || !spec.subtitle.trim()) throw new Error('transform: subject, title and subtitle are required');
  if (!spec.process.label.trim() || !spec.process.detail.trim() || !spec.law.trim()) throw new Error('transform: the mechanism needs a label, a detail and a law');
  if (spec.inputs.length < 1 || spec.inputs.length > 4) throw new Error('transform: require 1…4 inputs');
  if (spec.outputs.length < 1 || spec.outputs.length > 4) throw new Error('transform: require 1…4 outputs');
  if (spec.conserved.length < 1 || spec.conserved.length > 4) throw new Error('transform: require 1…4 conserved quantities');
  const ids = new Set<string>();
  for (const item of [...spec.inputs, ...spec.outputs]) {
    if (!/^[a-z][a-z0-9-]*$/.test(item.id)) throw new Error(`transform: invalid item id ${item.id}`);
    if (ids.has(item.id)) throw new Error(`transform: duplicate item id ${item.id}`);
    ids.add(item.id);
    if (!item.label.trim() || item.label.length > 24) throw new Error(`transform: item ${item.id} needs a label of 1…24 characters`);
    for (const [quantity, amount] of Object.entries(item.carries ?? {})) {
      if (!spec.conserved.includes(quantity)) throw new Error(`transform: item ${item.id} carries undeclared quantity ${quantity}`);
      if (!Number.isFinite(amount) || amount < 0) throw new Error(`transform: item ${item.id} carries an invalid amount of ${quantity}`);
    }
  }
  if (spec.outputs.some((item) => item.driver)) throw new Error('transform: only an input may be a driver');
  for (const quantity of spec.conserved) {
    const totals = totalsFor(spec, quantity);
    if (totals.input === 0 && totals.output === 0) throw new Error(`transform: nothing carries ${quantity}`);
    if (totals.input !== totals.output) {
      throw new Error(`transform: ${quantity} is declared conserved but ${totals.input} enters and ${totals.output} leaves`);
    }
  }
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`transform: this mechanism needs at least ${Math.ceil(required)}px of height`);
}

function totalsFor(spec: TransformSpec, quantity: string): { input: number; output: number } {
  const sum = (items: TransformItem[]): number => items.reduce((total, item) => total + (item.driver ? 0 : item.carries?.[quantity] ?? 0), 0);
  return { input: sum(spec.inputs), output: sum(spec.outputs) };
}
